import Typography from '@mui/material/Typography';
import { Checkbox, FormControlLabel, Grid, TextField, Box, Alert } from '@mui/material';
import CkeckboxRolesSkeleton from '../../../skeleton/CkeckboxRolesSkeleton';
import TextEllipsis from '../../../text/TextEllipsis';
import useFetchData from '../../../../hooks/useFetchData';
import InputDateUTC from '../../../FormControl/input/InputDateUTC';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import InputDateTimeUTC from '../../../FormControl/input/InputDateTimeUTC';
import ButtonStartIcon from '../../../button/ButtonStartIcon';
import DialogAttachMedia from '../../../dialog/DialogAttachMedia';
import ChipMediaFeed from '../../../chip/ChipMediaFeed';
import AutocompleteField from '../../../FormControl/input/AutocompleteField';
import './Form.css';
import TextEditor from '../../../TextEditor/TextEditor';

//TODO: Harcoded post types, move to backend
export const postTypes = [
  {
    id: 1,
    value: 'achievement',
    label: 'Achievement'
  },
  {
    id: 2,
    value: 'announcement',
    label: 'Announcement'
  },
  {
    id: 3,
    value: 'document',
    label: 'Document'
  },
  {
    id: 4,
    value: 'task',
    label: 'Task'
  }
];

const Form = ({ form = {}, errors = {}, onChange }) => {
  const [VisibilitySettingsOpen, setVisibilitySettingsOpen] = useState(false);
  const { isLoading: isLoadingRoles, data: dataRoles = [] } = useFetchData('roles/standard');
  const { isLoading: isLoadingYears, data: dataYears = [] } = useFetchData('setting/year');
  const [modal, setModal] = useState(null);

  // Check if any year-level-enabled role is selected
  const yearLevelRoles = ['Teacher', 'Student', 'Parent'];
  const hasYearLevelRole = form?.viewRoles?.some((role) =>
    yearLevelRoles.some((yrRole) => role?.name?.toLowerCase() === yrRole.toLowerCase())
  );

  const onSaveMedia = async (type, data) => {
    const currentAttachment = form.attachment || [];
    if (type === 'add') {
      onChange('attachment', [
        ...currentAttachment,
        {
          id: data._id || new Date().getTime(),
          ...data
        }
      ]);
    } else {
      const attachment = currentAttachment
        .map((item) => ({ id: item._id || item.id, ...item }))
        .filter((item) => item.id !== data.id);
      onChange('attachment', attachment);
    }
  };

  return (
    <div className='grid items-center gap-4'>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', width: '100%' }}>
        <Box sx={{ width: '70%' }}>
          <TextField
            id='title'
            name='title'
            value={form?.title}
            error={errors?.title}
            helperText={errors?.title}
            onChange={(e) => onChange('title', e.target.value)}
            fullWidth
            label='Enter title'
          />
        </Box>

        <Box sx={{ width: '30%' }}>
          <AutocompleteField
            id={`autocomplete-post-type`}
            name={'postType'}
            placeholder='Select type'
            options={postTypes}
            error={errors?.postType}
            helperText={errors?.postType}
            onChange={(e) => onChange('postType', e)}
            value={form?.postType?.label || ''}
            variant='outlined'
            className={'feed-form-post-type-autocomplete'}
            clearOnEscape={false}
            disableClearable
          />
        </Box>
      </Box>

      <TextEditor
        id='content'
        content={form?.content}
        setContent={(value) => onChange('content', value)}
        isEditable={true}
        showMenuBar={true}
        placeholder='Enter content here...'
        error={errors?.content}
        typeUploadFolder='feed'
      />

      <div className='border-t border-b py-4 border-muted'>
        <ButtonStartIcon
          onClick={() => setModal({ form: {}, type: 'add' })}
          color='primary'
          startIcon={'link'}
        >
          Attachments
        </ButtonStartIcon>
        {form.attachment && form.attachment.length > 0 && (
          <div className='flex flex-wrap gap-4 items-center py-2'>
            {form.attachment.map((data, index) => {
              return (
                <ChipMediaFeed
                  key={`attachment-${index}-${data._id}-${data.type}`}
                  index={index}
                  data={data}
                  error={errors?.attachment?.[data.name || data.name]}
                  onDelete={(data) => onSaveMedia('delete', data)}
                />
              );
            })}
          </div>
        )}
        {errors && errors.attachment && (
          <Alert severity='error' onClose={() => setInputErrors({ attachment: null })}>
            {'One or more attachments failed to upload.'}
          </Alert>
        )}
        {modal && (
          <DialogAttachMedia
            open={!!modal}
            onClose={() => setModal(false)}
            formData={modal.form}
            onSave={(data) => onSaveMedia(modal.type, data)}
            type={modal.type}
          />
        )}
      </div>

      {/* Roles Selection */}
      <div className='grid grid-cols-6 gap-2'>
        <Typography variant='subtitle1' color='primary' className='col-span-full'>
          Select Roles to View This Feed:
        </Typography>
        {errors?.viewRoles && (
          <Typography variant='body2' color='error' className='col-span-full'>
            {errors.viewRoles}
          </Typography>
        )}
        {isLoadingRoles ? (
          <CkeckboxRolesSkeleton />
        ) : (
          dataRoles?.map((role, index) => (
            <div key={role._id} className='flex items-center'>
              <FormControlLabel
                control={
                  <Checkbox
                    id={`checkbox-roles-${index}-${role?._id}`}
                    checked={form?.viewRoles?.some((r) => r?.id === role?.id) ? true : false}
                    name={role?._id}
                    onChange={(e) => {
                      let updatedRoles = form?.viewRoles ? [...form.viewRoles] : [];

                      if (e.target.checked) {
                        if (!updatedRoles?.some((r) => r?.id === role?.id)) {
                          updatedRoles.push(role);
                        }
                      } else {
                        updatedRoles = updatedRoles.filter((r) => r?.id !== role?.id);

                        // Clear year levels if no year-level-enabled roles are selected
                        const stillHasYearLevelRole = updatedRoles.some((r) =>
                          yearLevelRoles.some((yrRole) => r?.name?.toLowerCase() === yrRole.toLowerCase())
                        );
                        if (!stillHasYearLevelRole) {
                          onChange('viewYearLevels', []);
                        }
                      }

                      onChange('viewRoles', updatedRoles);
                    }}
                    color='primary'
                  />
                }
                label={<TextEllipsis id={`text-roles-${index}-${role._id}`}>{role.name}</TextEllipsis>}
              />
            </div>
          ))
        )}
      </div>

      {/* Year Levels Selection - Only show if Teacher, Student, or Parent is selected */}
      {hasYearLevelRole && (
        <div className='grid grid-cols-6 gap-2 mt-4'>
          <Typography variant='subtitle1' color='primary' className='col-span-full'>
            Select Year Levels to View This Feed: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          {errors?.viewYearLevels && (
            <Typography variant='body2' color='error' className='col-span-full'>
              {errors.viewYearLevels}
            </Typography>
          )}
          {isLoadingYears ? (
            <CkeckboxRolesSkeleton />
          ) : (
            dataYears?.map((year, index) => (
              <div key={year._id} className='flex items-center'>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={`checkbox-years-${index}-${year?._id}`}
                      checked={form?.viewYearLevels?.some((y) => y?.name === year?.name) ? true : false}
                      name={year?._id}
                      onChange={(e) => {
                        let updatedYears = form?.viewYearLevels ? [...form.viewYearLevels] : [];

                        if (e.target.checked) {
                          if (!updatedYears?.some((y) => y?.name === year?.name)) {
                            updatedYears.push(year);
                          }
                        } else {
                          updatedYears = updatedYears.filter((y) => y?.name !== year?.name);
                        }

                        onChange('viewYearLevels', updatedYears);
                      }}
                      color='primary'
                    />
                  }
                  label={
                    <TextEllipsis id={`text-years-${index}-${year._id}`}>
                      {year.name || year.label || `Year ${year.position}`}
                    </TextEllipsis>
                  }
                />
              </div>
            ))
          )}
        </div>
      )}

      {/* Visibility Settings */}
      <div>
        <Typography
          variant='subtitle1'
          color='primary'
          onClick={() => setVisibilitySettingsOpen(!VisibilitySettingsOpen)}
          className='cursor-pointer bg-loading px-4 py-2 '
        >
          <FontAwesomeIcon icon='cog' /> Visibility Settings
        </Typography>
        {VisibilitySettingsOpen && (
          <div className='space-y-4 bg-loading px-4 py-2'>
            <InputDateTimeUTC
              label='Publish at'
              type='datetime-local'
              name='pusblishedAt'
              format='DD/MMM/YYYY HH:mm a'
              value={form?.pusblishedAt}
              variant='outlined'
              onChange={(value) => onChange('pusblishedAt', value)}
            />
            <div className='flex gap-2'>
              <TextField
                label='Min days visible'
                type='number'
                value={form?.minDays ?? ''}
                onChange={(e) =>
                  onChange('minDays', e.target.value === '' ? null : parseInt(e.target.value, 10))
                }
                InputProps={{ inputProps: { min: 0 } }}
                error={errors?.minDays}
                helperText={errors?.minDays || 'Minimum number of days the feed should be visible'}
              />
              <InputDateUTC
                variant='outlined'
                label='Expire at'
                name='expireAt'
                minDate={form?.publishedAt}
                value={form?.expireAt}
                isStartOfTheDay={false}
                onChange={(value) => onChange('expireAt', value)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Form;
